import { pick } from 'lodash';
import { descend, prop, sort, sortBy } from 'ramda';

import { ELIGIBILITY_STEP_STATUS, ELIGIBILITY_STEPS } from './constants';

export const getEligibilityStepsStatus = (apiEligibilityStatus) => {
  const stepsStatus = [
    {
      id: ELIGIBILITY_STEPS.ACTIVE_FINANCING,
      status: apiEligibilityStatus.hasActiveLoan
        ? ELIGIBILITY_STEP_STATUS.CRITICAL_REQUIRED
        : ELIGIBILITY_STEP_STATUS.DONE,
    }, {
      id: ELIGIBILITY_STEPS.CREDIT_TO_CASH_RATIO,
      status: apiEligibilityStatus.meetsSalesRequirement
        ? ELIGIBILITY_STEP_STATUS.DONE
        : ELIGIBILITY_STEP_STATUS.REQUIRED,
    }, {
      id: ELIGIBILITY_STEPS.BARBER_BANK_ACCOUNTS,
      status: apiEligibilityStatus.hasBankAccountForAllBarbers
        ? ELIGIBILITY_STEP_STATUS.DONE
        : ELIGIBILITY_STEP_STATUS.REQUIRED,
    }, {
      id: ELIGIBILITY_STEPS.COMMISSION_SPLITS,
      status: apiEligibilityStatus.hasCommissionForAllBarbers
        ? ELIGIBILITY_STEP_STATUS.DONE
        : ELIGIBILITY_STEP_STATUS.REQUIRED,
    },
  ];

  return sortBy(prop('status'))(stepsStatus);
};

export const getOverallEligibilityStatus = eligibilityStepsStatus =>
  // eligibilityStepsStatus is already sorted by status, so the top one will be equal to overall one
  eligibilityStepsStatus[0].status;

export const isAutoPayoutPaused = (apiEligibilityStatus, shop) =>
  (shop.dailyPayroll || shop.commissionSplits)
    && (!apiEligibilityStatus.hasBankAccountForAllBarbers || !apiEligibilityStatus.hasCommissionForAllBarbers);

export const statusClassName = status => ({
  'c-autopayout__status-critical': status === ELIGIBILITY_STEP_STATUS.CRITICAL_REQUIRED,
  'c-autopayout__status-required': status === ELIGIBILITY_STEP_STATUS.REQUIRED,
  'c-autopayout__status-done': status === ELIGIBILITY_STEP_STATUS.DONE,
});

export const getIsDailyPayrollEnabled = (shop) => {
  if (!shop) {
    return false;
  }

  if (shop.dailyPayroll && shop.dailyPayrollDisabledOn) {
    return false;
  }
  if (!shop.dailyPayroll && shop.dailyPayrollEnabledOn) {
    return true;
  }

  return shop.dailyPayroll;
};

export const getIsCommissionSplitsEnabled = (shop) => {
  if (!shop) {
    return false;
  }

  if (shop.commissionSplits && shop.commissionSplitsDisabledOn) {
    return false;
  }
  if (!shop.commissionSplits && shop.commissionSplitsEnabledOn) {
    return true;
  }

  return shop.commissionSplits;
};

export const isAutoPayoutEnabled = (shop) => getIsDailyPayrollEnabled(shop) || getIsCommissionSplitsEnabled(shop);

export const getInitialAutoPayoutValues = (shop) => {
  const initialValues = pick(shop, [
    'commissionSplitsShopPaysFees',
    'commissionSplitsCashToBarbersServices',
    'commissionSplitsCashToBarbersProducts',
    'commissionPayoutDayOfWeek',
  ]);

  initialValues.dailyPayroll = getIsDailyPayrollEnabled(shop);
  initialValues.commissionSplits = getIsCommissionSplitsEnabled(shop);

  return initialValues;
};

export const dailyPayrollTurnedOff = (formData, shop) => !formData.dailyPayroll && shop.dailyPayroll;
export const commissionSplitsTurnedOff = (formData, shop) => !formData.commissionSplits && shop.commissionSplits;

export const getAutoPayoutUpdate = (formData) => {
  const update = { ...formData };

  if (update.commissionSplits) {
    update.commissionPayoutPeriodInDays = 7;
  }

  if (!update.commissionSplits && update.commissionPayoutDayOfWeek === null) {
    delete update.commissionPayoutDayOfWeek;
  }

  return update;
};

export const getPopulatedBarbersWithoutBankAccount = (barbersWithEligibilityIssues, allBarbersById) =>
  barbersWithEligibilityIssues
    .filter(barber => !barber.hasBankAccount)
    .map(barber => ({ ...barber, photos: allBarbersById[barber.id]?.photos }));

export const getNotifiedBarbersResult = (barbersNotifiedApiResult, populatedBarbersWithIssues) => {
  const notifiedBarbersResult = populatedBarbersWithIssues
    .map(barber => ({
      ...barber,
      notified: !(barbersNotifiedApiResult.errors?.some(errorBarber => errorBarber.barberId === barber.id)),
    }));
  return sort(descend(prop('notified')), notifiedBarbersResult);
};
